<script setup lang="ts">
import { ref } from 'vue';

import type { HotelDetailsEventSource } from '@apps/main/new-desktop/core/models';

import type {
  HotelDetailsFullscreenMasonryGalleryData,
  Stay,
  StayId,
} from '../../../models';

import HotelStayCard from './HotelStayCard.vue';

interface GridLayoutHotelDetailsStaysSectionProps {
  alternativeStays: Stay[];
  selectedStay?: Stay;
  source: HotelDetailsEventSource;
}
const props = defineProps<GridLayoutHotelDetailsStaysSectionProps>();

interface GridLayoutHotelDetailsStaysSectionEmits {
  (eventName: 'stay-selected', stayId: StayId): void;
  (eventName: 'more-information', stay: Stay): void;
  (
    eventName: 'open-gallery',
    data: HotelDetailsFullscreenMasonryGalleryData
  ): void;
}

const emit = defineEmits<GridLayoutHotelDetailsStaysSectionEmits>();

const root = ref<HTMLElement>();

defineExpose({
  root,
});
</script>

<template>
  <div
    id="hotel-details-stays"
    ref="root"
    class="grid-layout-hotel-details-stays-section"
  >
    <HotelStayCard
      v-if="props.selectedStay"
      :stay="props.selectedStay"
      :source="props.source"
      @more-information="emit('more-information', props.selectedStay)"
      @stay-selected="emit('stay-selected', $event)"
      @open-gallery="emit('open-gallery', $event)"
    />

    <HotelStayCard
      v-for="(stay, index) in props.alternativeStays"
      :key="index"
      :show-select-button="true"
      :stay="stay"
      :source="props.source"
      @stay-selected="emit('stay-selected', $event)"
      @more-information="emit('more-information', stay)"
      @open-gallery="emit('open-gallery', $event)"
    />
  </div>
</template>

<style scoped lang="scss">
.grid-layout-hotel-details-stays-section {
  padding: var(--spacing-16);

  display: grid;
  grid-template-rows: auto;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: var(--spacing-16) var(--spacing-24);

  @include for-medium-devices-max {
    grid-template-columns: repeat(2, 1fr);
  }
  @include for-small-devices-max {
    grid-template-columns: repeat(1, 1fr);
  }
  @include for-large-devices-min {
    padding: 0;
  }
}
</style>
